import React from 'react'

import { Avatar, type AvatarBadgeProps, Box, Text } from '@factorialco/deprecated-design-system'

import type { Access } from 'modules/core/components/AccessSelector/types'
import TooltipPortal from 'modules/core/components/DraggableNode'
import UserAvatar from 'modules/core/components/UserAvatar'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'
import i18n from 'modules/core/lib/i18n'

import { approverTooltip } from 'modules/approvals/lib/materializedApprovalFlows'

interface Props {
  accessToApprove?: Access | null
  role?: string | null
  roleInitials: string
  badge?: AvatarBadgeProps
  selectionCriteria?: string | null
}

function isTerminated(access?: Access | null): boolean {
  const employee = access?.employee
  const terminatedOn = employee?.terminatedOn
  return !!employee && !!terminatedOn && new Date(terminatedOn).getTime() < Date.now()
}

// joined is confirmed_at != null
function isActive(access: Access): boolean {
  return !isTerminated(access) && !!access.invitedOn && access.isConfirmed
}

export const ApproverWithBadge = ({
  accessToApprove,
  role,
  roleInitials,
  badge,
  selectionCriteria,
}: Props) => {
  const [open, setOpen] = React.useState(false)
  const coords = useMouseCoords()

  const terminatedLabel = i18n.t('settings.approvals.approval_group.details.terminated_approver')
  const notActiveLabel = i18n.t('settings.approvals.approval_group.details.not_active_approver')

  const isTerminatedOrNotActive =
    accessToApprove && (isTerminated(accessToApprove) || !isActive(accessToApprove))
  const label = isTerminated(accessToApprove) ? terminatedLabel : notActiveLabel

  const filteredBadge = isTerminatedOrNotActive
    ? ({
        label: label,
        icon: 'alert',
      } as const)
    : badge

  const approverLabel = approverTooltip(accessToApprove?.fullName || role, selectionCriteria)

  return (
    <>
      <span
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{ cursor: 'pointer' }}
      >
        <Box flexDirection="row" gap="s16" alignItems="center" justifyContent="spaceBetween">
          {accessToApprove ? (
            <UserAvatar user={accessToApprove} size="s32" badge={filteredBadge} />
          ) : (
            <Avatar size="s32" label={roleInitials} badge={filteredBadge} />
          )}
        </Box>
      </span>
      {open && (
        <TooltipPortal coordinates={coords}>
          <Box background="grey1000" borderRadius={{ all: 'abs008' }} padding="s16" maxWidth="s200">
            <Text.Normal color="grey000White">{approverLabel}</Text.Normal>
          </Box>
        </TooltipPortal>
      )}
    </>
  )
}
